import { HttpParams } from "@angular/common/http";
import { DateService, DateUnit } from "@app/services/date.service";
import { DayOfWeek } from "@enums/DayOfWeek";
import { ReasonCode } from "@enums/ReasonCode";
import { AccountUserType } from "@enums/UserType";
import { IFilterOptions } from "@interfaces/filters/IFilterOptions";
import { ICombinedPreferences } from "@interfaces/ICombinedPreferences";
import { IComboBox } from "@interfaces/IComboBox";
import { GeofenceCategory } from "@interfaces/IGeofences";
import { IPreferencesContent } from "@interfaces/IPreferencesContent";
import { IUser } from "@interfaces/IUser";
import { ReportTypesNavLabels } from "@reports/enums/ReportTypesNavLabels";
import { IReportHistory } from "@reports/models/IDynamicReportLog";
import { ICollatedHeader } from "@reports/models/IReportTableDefinition";
import { AccountService } from "@services/account.service";
import { AccountFeatureService } from "@services/accountFeature.service";
import { CacheService } from "@services/cache.Service";
import { ConstantsService } from "@services/constantsService";
import { CurrentUserService } from "@services/currentUser.service";
import { PreferencesService, Section } from "@services/preferences.service";
import { SessionService } from "@services/session.service";
import { TranslationService } from "@services/translation.service";
import { UnitConversionType } from "@services/unitConversion.service";
import { VeriDate } from "@services/veriDate.service";
import * as angular from "angular";
import copy from "fast-copy/dist/fast-copy.esm.js";
import * as moment from "moment-timezone";
import { SettingsName } from "src/app/class/SettingsName";
import { AlcolockEventGroup } from "../../tacho/alcolockCompliance/alcolock.enums";
import { ColumnDataType } from "../enums/ColumnDataType";
import { ReportDataFunction } from "../enums/ReportDataFunction";
import { MeasurementCategoryType } from "../enums/ReportMeasurementType";
import { ReportTableRowDataSource } from "../enums/ReportTableRowDataSource";
import { SchedulingFrequency } from "../enums/SchedulingFrequency";
import { TimeFilterType } from "../enums/TimeFilterType";
import { IColumnDefinition, IReportDataFunctionDefinition } from "../models/IColumnDefinition";
import { IColumnHeader } from "../models/IColumnHeader";
import { IReportTypeToCustomParameterMapping, IRowDataSourceToCustomParameterMapping } from "../models/ICustomParameterMapping";
import { IDataSourceDefinition } from "../models/IDataSourceDefinition";
import { IDynamicReportSchedule } from "../models/IDynamicReportSchedule";
import { IHtmlDocument } from "../models/IHtmlDocument";
import { IDataDefinition, IReportDefinition, IReportDefinitionHistory } from "../models/IReportDefinition";
import { IReportParameterDefinition, IReportParameters, ReportParameterType, ReportParameterValueType } from "../models/IReportParameters";
import { VehicleType } from "@app/enum/VehicleType.enum";
import { enumNames } from "@app/filters/enumNames.filter";

export interface IColumnFilterOptions {
    canFilterColumn?: boolean;
    showFilterValueOperators?: boolean;
    showTimeFilter?: boolean;
    filterTimeHour?: number;
    filterTimeMinute?: number;

    // display DOM elements
    displayTimeFilterControls?: boolean;
    displayFilterValueControl?: boolean;
    displayValueContainsControl?: boolean;
    displayGenericFilterValueControls?: boolean;
}

export interface IStandardColumns {
    rowTitleColumn: IColumnHeader,
    stringColumn: IColumnHeader,
    doubleColumn: IColumnHeader,
    dateColumn: IColumnHeader,
    durationColumn: IColumnHeader
}

export interface IReportCategory {
    title: string;
    icon: string;

    columns: IColumnHeader[];

    hide?: boolean;
    showColumns?: boolean;
}

export interface IDateRangeOption {
    Before?: Date | string | null;
    After?: Date | string | null;
}

export interface IRangeOption<T extends number | string> {
    GreaterThan?: T | null;
    LessThan?: T | null;
}

export interface IReportScheduleQueryOptions {
    Id?: number | null;
    Name?: string;
    Frequency?: SchedulingFrequency[];
    ExportType?: ExportType[];
    RunDate?: IRangeOption<number>;
    UserId?: number | null;
    AccountId?: number | null;
    ReportDefinitionId?: number | null;
    RetryCount?: IRangeOption<number>;
    GenerationHour?: IRangeOption<number>;
    LastScheduledQueueTime?: IDateRangeOption;
    NextScheduledQueueTime?: IDateRangeOption;
    IsActiveSchedule?: boolean | null;
    IsActiveUser?: boolean | null;
    IsActiveAccount?: boolean | null;

    invalidateCache?: boolean;
    minsToLive?: number;
}

export interface IHeaderSortSettings {
    headerId: number;
    ascending: boolean;
}

export enum ExportType {
    PDF,
    Excel,
    CSV
}

export enum DateRange {
    Today = 0,
    Yesterday = 1,
    Week = 2,
    Month = 3,
    Custom = 4,
    ThisWeek = 5,
    LastWeek = 6,
    Latest3Weeks = 7,
    ThisMonth = 8,
    LastMonth = 9
}

export class DynamicReportService {
    apiUrl: string;
    private reportDefenitionsCacheName = "reportDefenitionsCacheName";

    static $inject = ["$q", "$http", "$document", "$timeout", "constantsService", "currentUserService", "accountService", "translationService", "cacheService", "accountFeatureService", "preferencesService", "sessionService", "veriDate", "dateService"];
    constructor(
        public readonly $q: angular.IQService,
        private readonly $http: angular.IHttpService,
        private readonly $document: angular.IDocumentService,
        private readonly $timeout: angular.ITimeoutService,
        private readonly constantsService: ConstantsService,
        private readonly currentUserService: CurrentUserService,
        private readonly accountService: AccountService,
        private readonly translationService: TranslationService,
        private readonly cacheService: CacheService,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly preferencesService: PreferencesService,
        private readonly sessionService: SessionService,
        private readonly veriDate: VeriDate,
        private readonly dateService: DateService,

    ) {
        this.apiUrl = this.constantsService.getApiUrl();
        this.cacheService.createCache(this.reportDefenitionsCacheName, { maxAge: 30 * 60 * 1000 });
    }

    createReportTemplate(): IReportDefinition {
        const newReportDefinition: IReportDefinition = {
            Active: true,
            Title: null,
            RowDataSource: ReportTableRowDataSource.Vehicle,
            SubRowDataSource: ReportTableRowDataSource.None,
            Portrait: true,
            TableDefinition: {
                Headers: [],
                Rows: [],
                SummarySections: []
            },
            newReport: true,
            Shared: false
        };

        return newReportDefinition;
    }

    createReportScheduleTemplate(reportId: number = 0): IDynamicReportSchedule {
        const newReportSchedule: IDynamicReportSchedule = {
            Name: "",
            Frequency: SchedulingFrequency.Weekly,
            ReportDefinitionId: reportId,
            ExportType: ExportType.PDF,
            RepeatOn: [DayOfWeek.Monday],
            Active: true,
            SendTo: [""],
        };

        this.parseScheduleParametersJson(newReportSchedule);
        this.parseSchedulePreferencesJson(newReportSchedule);

        return newReportSchedule;
    }

    clearParams(reportDefinition: IReportDefinition): void {
        reportDefinition.newReport = true;
        reportDefinition.Id = 0;

        reportDefinition.reportPdfUrl = null;
        reportDefinition.reportExcelUrl = null;
        reportDefinition.reportCsvUrl = null;

        reportDefinition.Parameters.Vehicle = null;
        reportDefinition.Parameters.VehicleGroup = null;
        reportDefinition.Parameters.VehicleGroupId = null;
        reportDefinition.Parameters.VehicleId = null;

        reportDefinition.Parameters.Driver = null;
        reportDefinition.Parameters.DriverGroup = null;
        reportDefinition.Parameters.DriverGroupId = null;
        reportDefinition.Parameters.DriverId = null;

        reportDefinition.Parameters.Start = null;
        reportDefinition.Parameters.End = null;

        reportDefinition.Parameters.Geofence = null;
        reportDefinition.Parameters.GeofenceGroup = null;
        reportDefinition.Parameters.GeofenceGroupId = null;
        reportDefinition.Parameters.GeofenceId = null;

        reportDefinition.Parameters.TagIds = null;

        reportDefinition.Parameters.TimeFilterStart = null;
        reportDefinition.Parameters.TimeFilterEnd = null;
        reportDefinition.Parameters.TimeFilterType = null;

        reportDefinition.Parameters.TranslatedTitle = null;
    }

    getStandardColumns(): angular.IPromise<IStandardColumns> {
        return this.getColumnHeaders().then((columns) => {
            var reportColumns = columns;

            return {
                rowTitleColumn: this.findColumn("RowTitle", "SharedReport", reportColumns),
                stringColumn: this.findColumn("String", "SharedReport", reportColumns),
                doubleColumn: this.findColumn("Double", "SharedReport", reportColumns),
                dateColumn: this.findColumn("Date", "SharedReport", reportColumns),
                durationColumn: this.findColumn("Duration", "SharedReport", reportColumns)
            }
        });
    }

    getLatestReportHistory(invalidateCache = false): angular.IPromise<IReportHistory[]> {
        return this
            .$http({
                url: "/api/reports/logs/latest",
                method: "GET",
                cache: true,
                params: { minsToLive: 60, invalidateCache: invalidateCache } // 60 min cache
            })
            .then((response: angular.IHttpResponse<IReportHistory[]>) => {

                var successfulLogs: IReportHistory[] = [];

                response.data.forEach(reportLog => {
                    if (!reportLog.Success) {
                        return;
                    }

                    this.formatReportLog(reportLog);

                    successfulLogs.push(reportLog);
                });

                successfulLogs.sort((x, y) => {
                    return y.Id - x.Id;
                });

                return successfulLogs;
            });
    }

    getLogCounts(invalidateCache = false): angular.IPromise<number[]> {
        return this
            .$http({
                url: "/api/reports/logs/count",
                method: "GET",
                cache: true,
                params: { minsToLive: 60, invalidateCache: invalidateCache } // 60 min cache
            })
            .then((response: angular.IHttpResponse<number[]>) => {
                return response.data;
            });
    }

    getReportHistory(reportId: number, invalidateCache = false): angular.IPromise<IReportHistory[]> {
        if (!reportId) {
            return this.$q.resolve<IReportHistory[]>([]);
        }

        return this
            .$http({
                url: `/api/reports/logs/${reportId}`,
                method: "GET",
                cache: true,
                params: { minsToLive: 60, invalidateCache: invalidateCache } // 60 min cache
            })
            .then((response: angular.IHttpResponse<IReportHistory[]>) => {

                var successfulLogs: IReportHistory[] = [];

                response.data.forEach(reportLog => {
                    if (!reportLog.Success) {
                        return;
                    }

                    this.formatReportLog(reportLog);

                    successfulLogs.push(reportLog);
                });

                successfulLogs.sort((x, y) => {
                    return y.Id - x.Id;
                });

                return successfulLogs;
            });
    }

    getReportScheduleLogs(scheduleId: number, invalidateCache = false): angular.IPromise<IReportHistory[]> {
        return this
            .$http({
                url: `/api/reports/logs/schedule/${scheduleId}`,
                method: "GET",
                cache: true,
                params: { minsToLive: 60, invalidateCache: invalidateCache } // 60 min cache
            })
            .then((response: angular.IHttpResponse<IReportHistory[]>) => {

                var successfulLogs: IReportHistory[] = [];

                response.data.forEach(reportLog => {
                    if (!reportLog.Success) {
                        return;
                    }

                    this.formatReportLog(reportLog);

                    successfulLogs.push(reportLog);
                });

                successfulLogs.sort((x, y) => {
                    return y.Id - x.Id;
                });

                return successfulLogs;
            });
    }

    parametersHaveDatesSet(reportParameters: IReportParameters): boolean {
        if (!(reportParameters.Start instanceof Date) || !(reportParameters.End instanceof Date)) {
            return false
        }
        return true;
    }

    formatParametersToUTCTimezone(reportParameters: IReportParameters): void {
        if (this.isNullOrDefault(reportParameters)) {
            return;
        }

        if (this.parametersHaveDatesSet(reportParameters) && reportParameters.TimeFilterType != TimeFilterType.None && reportParameters.TimeFilterStart && reportParameters.TimeFilterEnd) {
            var timeStart = this.dateService.getDurationFromTimeSpan(reportParameters.TimeFilterStart);
            reportParameters.Start.setHours(timeStart.hours, timeStart.minutes, timeStart.seconds);

            var timeEnd = this.dateService.getDurationFromTimeSpan(reportParameters.TimeFilterEnd);
            reportParameters.End.setHours(timeEnd.hours, timeEnd.minutes, timeEnd.seconds);
        }

        reportParameters.TimeFilterStart = this.timespanToUTC(reportParameters.TimeFilterStart);
        reportParameters.TimeFilterEnd = this.timespanToUTC(reportParameters.TimeFilterEnd);
    }

    formatParametersToLocalTimezone(reportParameters: IReportParameters): void {
        if (!reportParameters) {
            return;
        }

        if (this.isNullOrDefault(reportParameters)) {

            reportParameters.Start = new Date(reportParameters.Start);
            reportParameters.End = new Date(reportParameters.End);

            return;
        }

        reportParameters.TimeFilterStart = this.timespanToLocal(reportParameters.TimeFilterStart);
        reportParameters.TimeFilterEnd = this.timespanToLocal(reportParameters.TimeFilterEnd);

        reportParameters.Start = new Date(reportParameters.Start);
        reportParameters.End = new Date(reportParameters.End);
    }

    populateReport(reportDefinition: IReportDefinition, preview: boolean = false): angular.IPromise<IReportHistory> {
        this.formatParametersToUTCTimezone(reportDefinition.Parameters);

        return this.$http(
            {
                method: "POST",
                url: `/api/analytical/populate/${preview}`,
                data: reportDefinition,
            })
            .then((result: angular.IHttpResponse<IReportHistory>) => {
                var reportLog = result.data;

                this.formatReportLog(reportLog);
                this.formatParametersToLocalTimezone(reportDefinition.Parameters)

                return this.$timeout(() => {
                    return reportLog;
                }, 500);
            });
    }

    previewReport(reportDefinition: IReportDefinition): angular.IPromise<IHtmlDocument> {
        reportDefinition = copy(reportDefinition);
        reportDefinition.Title = reportDefinition.Title || "New Report";

        return this.$http(
            {
                method: "POST",
                url: `/api/analytical/preview`,
                data: reportDefinition,
            })
            .then((result: angular.IHttpResponse<IHtmlDocument>) => {
                result.data.Footer = result.data.Footer.replace(`<span class="page">X</span>`, `<span class="page">1</span>`);
                result.data.Footer = result.data.Footer.replace(`<span class="topage">Y</span>`, `<span class="topage">1</span>`);

                return result.data;
            });
    }

    getColumnHeaders(): angular.IPromise<IColumnHeader[]> {
        return this
            .$http({
                url: `/api/reports/columnheaders`,
                method: "GET",
                cache: true
            })
            .then((response: angular.IHttpPromiseCallbackArg<IColumnHeader[]>) => {

                response.data.forEach(column => {
                    column.uniqueId = this.getUniqueColumnId(column);
                });

                return response.data;
            });
    }

    getUniqueColumnId(column: IColumnHeader): string {
        return `${column.ReportType}.${column.DataSourceValue}`;
    }

    setReportDefinitionColumnIds(reportDefinition: IReportDefinition): void {
        if (!reportDefinition) {
            console.warn("No report model")
            return;
        }

        if (!reportDefinition.TableDefinition) {
            console.warn("No table definition on report model")
            return;
        }

        if (!reportDefinition.TableDefinition?.Headers?.length) {
            console.warn("No table definition headers on report model")
            return;
        }

        reportDefinition.TableDefinition.Headers.forEach(column => {
            column.uniqueId = this.getUniqueColumnId(column);
        });
    }

    getDataSourceDefinitions(): angular.IPromise<IDataSourceDefinition[]> {
        return this
            .$http({
                url: `/api/reports/datasources`,
                method: "GET",
                cache: true
            })
            .then((response: angular.IHttpPromiseCallbackArg<IDataSourceDefinition[]>) => {
                return response.data;
            });
    }

    reportNameExists(reportName: string, existingReportId: number) {
        return this
            .$http({
                url: `/api/reports/exists/${existingReportId}?reportName=${encodeURIComponent(reportName)}`,
                method: "GET",
                cache: true
            })
            .then((response: angular.IHttpResponse<IColumnHeader[]>) => {
                return response.data;
            });
    }

    getDisplayColumnHeaders(): angular.IPromise<IColumnHeader[]> {
        const getColumnsPromise = this.getColumnHeaders();
        const getDataSourceDefinitionPromise = this.getDataSourceDefinitions();
        const accountPromise = this.accountService.getAccountData()

        return this.$q.all([getColumnsPromise, getDataSourceDefinitionPromise, accountPromise]).then(([columns, dataSourceDefinition, account]) => {

            columns = columns.filter(column => this.accountFeatureService.isReportTypeEnabled(account, column.ReportType))

            this.mapDataSourceDefinitionsToColumns(columns, dataSourceDefinition);
            this.formatColumnsForDisplay(columns);

            return columns;
        });
    }

    filterAvailableColumnHeaders(columnHeaders: IColumnHeader[]): IColumnHeader[] {
        if (!columnHeaders?.length) {
            console.warn("No columnHeaders provided");
            return;
        }

        const availableColumnHeaders: IColumnHeader[] = [];

        columnHeaders.forEach(column => {
            if (!(column.dataSourceDefinition
                && column.dataSourceDefinition.IsVisible
                && column.ColumnDefinition
                && column.ColumnDefinition.Format
                && column.ColumnDefinition.Format.IsVisible)) {
                return;
            }

            availableColumnHeaders.push(column);
        });

        return availableColumnHeaders;
    }

    getAvailableColumnHeadersForReportEditor(): angular.IPromise<any> {
        return this.getDisplayColumnHeaders().then((columnHeaders: IColumnHeader[] = []) => {
            const availableColumnHeaders: IColumnHeader[] = [];

            columnHeaders.forEach(column => {
                if (!(column.dataSourceDefinition
                    && column.dataSourceDefinition.IsVisible
                    && column.ColumnDefinition
                    && column.ColumnDefinition.Format
                    && column.ColumnDefinition.Format.IsVisible)) {
                    return;
                }

                availableColumnHeaders.push(column);
            });

            return availableColumnHeaders;
        });
    }

    findRowTitleColumn(columns: IColumnHeader[]): IColumnHeader {
        if (!columns?.length) {
            console.warn("No columns provided");
            return;
        }

        for (let i = 0; i < columns.length; i++) {
            const header = columns[i];

            if (header.IsRowTitle || header.DataSourceValue === "RowTitle") {
                return header;
            }
        }
    }

    mapDataSourceDefinitionsToColumns(columns: IColumnHeader[], dataSourceDefinition: IDataSourceDefinition[]) {
        columns.forEach(column => {
            dataSourceDefinition.forEach(dataSource => {
                if (column.ReportType === dataSource.ReportType) {
                    column.dataSourceDefinition = dataSource;
                }
            });
        });
    }

    formatColumnsForDisplay(columns: IColumnHeader[]) {
        columns.forEach(column => {

            if (!column.ColumnDefinition) {
                column.ColumnDefinition = {};
            }

            if (column.ColumnDefinition.Type) {
                column.ColumnDefinition.dataTypeDisplayName = this.getColumnTypeDisplayName(column.ColumnDefinition.Type);
            }

            if (!column.Title && column.DefaultTitle) {
                column.Title = column.DefaultTitle;
            }

            column.ColumnDefinition.supportedReportDataFunctionDefinitions = this.createDisplayDatafunctions(column.ColumnDefinition);

            if (column.dataSourceDefinition) {
                column.dataSourceDefinition.icon = this.mapIconForDataSource(column.dataSourceDefinition.DataSourceType);
            }

            if (column.DataSourceValue === "RowTitle") {
                return column.IsRowTitle = true;
            }
        });
    }

    mapIconForDataSource(dataSource: string) {
        let icon = "description";

        this.dataSourceIconMappings.forEach(mapping => {
            if (mapping.dataSource === dataSource) {
                icon = mapping.icon;
            }
        });

        if (icon === "description" || !icon) {
            console.log("No icon for", dataSource);
        }

        return icon;
    }

    dataSourceIconMappings: { dataSource: string, icon: string }[] = [
        { dataSource: "Camera", icon: "videocam" },
        { dataSource: "Maintenance", icon: "ballot" },
        { dataSource: "CameraDataSource", icon: "videocam" },
        { dataSource: "VideoStreamingDataSource", icon: "videocam" },
        { dataSource: "CommonEventDataSource", icon: /*"warning"*/"taxi_alert" },
        { dataSource: "GeofenceVisitDataSource", icon: "filter_tilt_shift" },
        { dataSource: "JourneyDataSource", icon: /*"timeline"*/"directions_car" },
        { dataSource: "SharedReportDataSource", icon: "category" },
        { dataSource: "StationaryLocationDataSource", icon: "local_parking" },
        { dataSource: "TachoDataSource", icon: "update" },
        { dataSource: "TemperatureDataSource", icon: "ac_unit" },
        { dataSource: "TemperatureOutOfBoundsDataSource", icon: "ac_unit" },
        { dataSource: "TmsDataSource", icon: "work" },
        { dataSource: "TrailerAssociationDataSource", icon: "link" },
        { dataSource: "VehicleDataSource", icon: "directions_car" },
        { dataSource: "DriverDataSource", icon: "person" },
        { dataSource: "VehicleMaintenanceDataSource", icon: "ballot" },
        { dataSource: "VehicleActivityDataSource", icon: "ballot" },
        { dataSource: "AlcolockDataSource", icon: "playlist_add_check" },
        { dataSource: "VehicleStatusDataSource", icon: "healing" },
        { dataSource: "VehicleInputDataSource", icon: "electric_car" },
        { dataSource: "EmptyRunningHistoryDataSource", icon: "local_shipping" },
        { dataSource: "AlertReviewDataSource", icon: "notifications" },
        { dataSource: "AlertRecordDataSource", icon: "notifications" },
        { dataSource: "VorRecordDataSource", icon: "report" },
        { dataSource: "CameraOnlineHistoryDataSource", icon: "videocam" },
        { dataSource: "VideoChannelDataSource", icon: "videocam" },
        { dataSource: "FormSubmissionDataSource", icon: "ballot" },
        { dataSource: "LatestTemperatureDataSource", icon: "ac_unit" },
        { dataSource: "DeviceParameterProfileDataSource", icon: "ballot" },
        { dataSource: "DrivingStyleDataSource", icon: "ballot" },
        { dataSource: "BrakePerformanceHistoryDataSource", icon: "construction" },
        { dataSource: "BrakePerformanceValueDataSource", icon: "construction" },
    ];

    createDisplayDatafunctions(columnDefinition: IColumnDefinition): IReportDataFunctionDefinition[] {
        var functionDefinitions: IReportDataFunctionDefinition[] = [];

        columnDefinition.SupportedReportDataFunctions.forEach(func => {
            var definition: IReportDataFunctionDefinition =
            {
                title: ReportDataFunction[func],
                function: func
            };

            switch (func) {
                case ReportDataFunction.Sum:
                    {
                        definition.title = "Total";
                        definition.description = `Gives a sum of all ${columnDefinition.Title}`;
                    }
            }

            functionDefinitions.push(definition);
        });

        return functionDefinitions;
    }

    restrictReportToSingleResource(report: IReportDefinition): boolean {
        return report.SubRowDataSource === ReportTableRowDataSource.TachoRecord || this.isTemperatureReport(report);
    }

    isTemperatureReport(report: IReportDefinition): boolean {
        return report.SubRowDataSource === ReportTableRowDataSource.TemperatureRecord || this.headersContainsReportType(report, "Temperature");
    }

    saveReportDefinition(reportDefinition: IReportDefinition): angular.IPromise<IReportDefinition> {
        const currentUserPromise = this.currentUserService.getCurrentUser();
        const getDataSourceDefinitionPromise = this.getDataSourceDefinitions();

        const reportPromise = this
            .$http({
                method: "POST",
                url: `/api/reports`,
                data: reportDefinition,
            })
            .then((response: angular.IHttpResponse<IReportDefinition>) => response.data);

        return this.$q.all([reportPromise, currentUserPromise, getDataSourceDefinitionPromise]).then(([report, currentUser, dataSourceDefinition]) => {

            report = { ...reportDefinition, ...report };

            this.mapDataSourceDefinitionsToColumns(report.TableDefinition.Headers, dataSourceDefinition);
            this.formatColumnsForDisplay(report.TableDefinition.Headers);
            this.formatReportForDisplay(report, currentUser);

            report.isSystemReport = false;
            report.canEdit = true;

            this.cacheService.addOrUpdateItem<IReportDefinition>(this.reportDefenitionsCacheName, report, (i1, i2) => i1.Id == i2.Id);

            return report
        });
    }

    deleteReportDefinition(definitionId: number): angular.IPromise<void> {
        return this.$http(
            {
                method: "DELETE",
                url: `/api/reports/${definitionId}/`,
            }).then(() => {

                this.cacheService.deleteItem<IReportDefinition>(this.reportDefenitionsCacheName, { Id: definitionId }, (i1, i2) => i1.Id == i2.Id);

                return;
            });
    }

    getReports(invalidateCache = false): angular.IPromise<IReportDefinition[]> {
        return this.cacheService.getOrRetrieveData<IReportDefinition[]>(this.reportDefenitionsCacheName, () => this.getReportsFromDB(), invalidateCache)
    }

    getReportsFromDB(invalidateCache = false): angular.IPromise<IReportDefinition[]> {
        const currentUserPromise = this.currentUserService.getCurrentUser();
        const getDataSourceDefinitionPromise = this.getDataSourceDefinitions();

        const reportsPromise = this
            .$http({
                url: "/api/reports",
                method: "GET"
            })
            .then((response: angular.IHttpResponse<IReportDefinition[]>) => {
                return response.data;
            });

        return this.$q.all([reportsPromise, currentUserPromise, getDataSourceDefinitionPromise]).then(([reportDefinitions, currentUser, dataSourceDefinition]) => {
            reportDefinitions.forEach(report => {

                const userPrefs = this.sessionService.getItem<ICombinedPreferences>(SettingsName.UserOptions);
                this.setReportPreferences(report, userPrefs);
                this.mapDataSourceDefinitionsToColumns(report.TableDefinition.Headers, dataSourceDefinition);
                this.formatColumnsForDisplay(report.TableDefinition.Headers);
                this.formatReportForDisplay(report, currentUser);
            });

            reportDefinitions.sort((x, y) => {
                return x.Title.localeCompare(y.Title);
            });

            return reportDefinitions;
        });
    }

    updateStockReports(): angular.IPromise<IReportDefinition[]> {
        return this
            .$http({
                url: "/api/reports/stock",
                method: "POST"
            })
            .then((response: angular.IHttpResponse<IReportDefinition[]>) => {
                return response.data;
            });
    }

    getReportType(report: IReportDefinition): ReportTypesNavLabels {
        if (report.isSystemReport) {
            return ReportTypesNavLabels.System;
        }
        else if (report.Shared) {
            return ReportTypesNavLabels.Shared;
        }

        return ReportTypesNavLabels.User;
    }

    formatReportForDisplay(report: IReportDefinition, currentUser: IUser) {
        report.isSystemReport = (!report.UserId && !report.AccountId && report.Shared);
        report.canEdit = (report.UserId === currentUser.Id || (report.Shared && currentUser.CurrentAccountUserMapping.Type === AccountUserType.Admin));

        report.reportNavType = this.getReportType(report);

        report.reportTypes = report.ReportType?.split(",");

        // Colate report header types
        report.TableDefinition.collatedHeaders = report.TableDefinition.Headers.reduce(
            (collatedHeaders, currentHeader) => {
                if (!currentHeader.dataSourceDefinition) {
                    return collatedHeaders;
                }

                var currentRunningTotal: ICollatedHeader = collatedHeaders.find(h => h.headerName == currentHeader.dataSourceDefinition.DataSourceType);

                if (!currentRunningTotal) {
                    currentRunningTotal = {
                        headerName: currentHeader.dataSourceDefinition.DataSourceType,
                        icon: currentHeader.dataSourceDefinition.icon,
                        headers: [],
                        count: 0
                    };

                    collatedHeaders.push(currentRunningTotal);
                }

                currentRunningTotal.count++;
                currentRunningTotal.headers.push(currentHeader);

                return collatedHeaders;
            }, []);

        report.TableDefinition.collatedHeaders.sort((x, y) => y.count - x.count);
        report.icon = report.TableDefinition.collatedHeaders.length ? report.TableDefinition.collatedHeaders[0].icon : null;
    }

    getReportSchedules(options: IReportScheduleQueryOptions = {}): angular.IPromise<IDynamicReportSchedule[]> {

        if (options && !options.minsToLive) {
            options.minsToLive = 60; // default 60 min cache
        }

        return this
            .$http({
                url: "/api/reports/schedules",
                method: "GET",
                cache: true,
                params: options as HttpParams
            })
            .then((response: angular.IHttpPromiseCallbackArg<IDynamicReportSchedule[]>) => {
                response.data.forEach(schedule => {
                    this.parseSchedulePreferencesJson(schedule);
                    this.parseScheduleParametersJson(schedule);
                    this.formatParametersToLocalTimezone(schedule.Parameters);
                });

                return response.data;
            });
    }

    saveSchedule(schedule: IDynamicReportSchedule): angular.IPromise<IDynamicReportSchedule> {
        let promise = this.$q.resolve();

        if (schedule.Preferences == null) {
            promise = this.preferencesService.getAllPreferences().then(combinedPrefs => {
                schedule.Preferences = combinedPrefs;
            });
        }

        if (schedule.Preferences.Timezone == null) {
            promise = this.preferencesService.getAllPreferences().then(combinedPrefs => {
                schedule.Preferences.Timezone = combinedPrefs.Timezone;
            });
        }

        return promise.then(() => {
            this.formatParametersToUTCTimezone(schedule.Parameters);

            this.stringifyScheduleParameters(schedule);
            this.stringifySchedulePreferences(schedule);

            return this.$http({
                method: "POST",
                url: "/api/reports/schedules",
                data: schedule,
            })
                .then((result: angular.IHttpResponse<IDynamicReportSchedule>) => {
                    const savedSchedule = result.data;
                    this.formatParametersToLocalTimezone(savedSchedule.Parameters);
                    this.parseScheduleParametersJson(savedSchedule);
                    return savedSchedule;
                });
        });
    }

    generate(report: IReportDefinition, exportType: ExportType): angular.IPromise<void> {
        if (report == null) {
            return this.$q.resolve();
        }

        return this.$http(
            {
                method: "POST",
                url: `/api/analytical/generate/${exportType}`,
                responseType: "blob",
                data: report,
            }).then((result: angular.IHttpResponse<Blob>) => {
                var fileURL = URL.createObjectURL(result.data);

                var a = this.$document[0].createElement('a');
                a.href = fileURL;
                a.target = '_blank';
                a.download = `${report.Title}${this.getDocumentExtension(exportType)}`;
                this.$document[0].body.appendChild(a);
                a.click();
                this.$document[0].body.removeChild(a);
            });
    }

    filterReportDefinitionHistory(reportDefinitions: IReportDefinitionHistory[], searchString: string): IReportDefinitionHistory[] {
        if (!searchString || searchString.length === 0) {
            return reportDefinitions;
        }

        searchString = searchString.toLowerCase();

        const filteredReportDefinitions: IReportDefinitionHistory[] = [];

        reportDefinitions.forEach(report => {
            let showReportDefinition = false;

            if (report.definition.Title.toLowerCase().indexOf(searchString) >= 0) {
                showReportDefinition = true;
            }

            if (showReportDefinition) {
                filteredReportDefinitions.push(report);
            }
        });

        return filteredReportDefinitions;
    }

    getCustomParameterDefinitions(reportDefinition: IReportDefinition): IReportParameterDefinition[] {
        if (!reportDefinition || !reportDefinition.TableDefinition) {
            return [];
        }

        const activeMappings: IReportTypeToCustomParameterMapping[] = [];

        reportDefinition.TableDefinition.Headers.forEach(header => {
            var existingMatch = false;

            activeMappings.forEach(mapping => {
                if (header.ReportType === mapping.reportType) {
                    existingMatch = true;
                }
            });

            if (existingMatch) {
                return;
            }

            this.reportTypeToCustomParameterMappings.forEach(mapping => {
                if (header.ReportType === mapping.reportType) {
                    activeMappings.push(copy(mapping));
                }
            });

        });

        var customParameters: IReportParameterDefinition[] = [];

        activeMappings.forEach(activeMapping => {
            activeMapping.mappings.forEach(mapping => {
                customParameters.push(mapping);
            });
        });

        const matches = this.rowDataSourceToCustomParameterMappings.filter(mapping => mapping.rowDataSource == reportDefinition.RowDataSource || mapping.rowDataSource == reportDefinition.SubRowDataSource)

        if (matches.length) {
            customParameters.push(...matches.flatMap(match => match.mappings));
        }

        this.setParameterDefinitionsFromReport(reportDefinition, customParameters);

        // Sort by type and then by conversion type
        customParameters.sort((a, b) => {
            // Put selects first
            if (a.valueType == ReportParameterValueType.Select) {
                return -1;
            }
            else if (b.valueType == ReportParameterValueType.Select) {
                return 1;
            }

            if (a.valueType == b.valueType) {
                return a.conversionType - b.conversionType;
            }

            return a.valueType - b.valueType;
        });

        // Filter duplicates (it can happen)
        customParameters = customParameters.reduce<IReportParameterDefinition[]>((a, b) => {
            if (a.filter(ap => ap.ParameterType == b.ParameterType).length == 0) {
                a.push(b);
            }

            return a;
        }, []);

        return customParameters;
    }

    getCustomParameterDefinitionsForValueType(reportDefinition: IReportDefinition, reportParameterValueType: ReportParameterValueType): IReportParameterDefinition[] {
        const customReportDefinitions: IReportParameterDefinition[] = this.getCustomParameterDefinitions(reportDefinition);

        if (!customReportDefinitions?.length) {
            return null;
        }

        return customReportDefinitions.filter(param => param.valueType === reportParameterValueType);
    }

    headersContainsReportType(reportDefinitions: IReportDefinition, reportType: string) {
        var containsType = false;

        reportDefinitions.TableDefinition.Headers.forEach(header => {
            if (header.ReportType === reportType) {
                containsType = true;
            }
        });

        return containsType;
    }

    requiresGeofenceSelector(reportDefinition: IReportDefinition): boolean {
        if (!reportDefinition || !reportDefinition.Parameters || !reportDefinition.Parameters.CustomParameters) {
            return false;
        }

        if (reportDefinition.Parameters.CustomParameters.filter(cp => cp.ParameterType === ReportParameterType.GeofenceCategory && cp.Value === GeofenceCategory.Zone).length) {
            return false;
        }

        return this.requiresSelector(reportDefinition, ["GeofenceVisit"]);
    }

    requiresDateSelector(reportDefinition: IReportDefinition): boolean {
        let requiresSelector = false;

        const reportTypes = ["VehicleStatus", "SharedReport", "VideoStreaming", "VideoChannel"];
        const reportsToExclude: string[] = ["76efb175-78f6-4271-98a3-47f4ea3f7895"];
        const latestParameterTypes = [ReportParameterType.LatestCameraOnlineHistory, ReportParameterType.LatestBrakePerformanceHistory, ReportParameterType.LatestBrakePerformanceValue];

        reportDefinition.TableDefinition.Headers.forEach(header => {
            if (!reportTypes.includes(header.ReportType)) {
                requiresSelector = true;
            }
        });

        const hasLatestParameter: boolean = !!reportDefinition.Parameters.CustomParameters.find(param => {
            return latestParameterTypes.includes(param.ParameterType) && param.Value && param.Active;
        })

        if (reportsToExclude.includes(
            reportDefinition.StaticStockReportId) ||
            hasLatestParameter) {
            requiresSelector = false;
        }

        return requiresSelector;
    }


    requiresGeofenceGroupSelector(reportDefinitions: IReportDefinition): boolean {
        return false;
    }

    getUniqueReportCategories(allColumns: IColumnHeader[]): IReportCategory[] {
        var categories: IReportCategory[] = [];

        allColumns.forEach(column => {

            const displayName = this.getColumnDataSourceName(column);
            const existingCategory = categories.find(c => c.title == displayName);

            if (existingCategory) {
                existingCategory.columns.push(column);
                return;
            }

            categories.push({
                title: displayName,
                icon: column.dataSourceDefinition ? column.dataSourceDefinition.icon : "category",
                columns: [column]
            });
        });

        // Sort categories
        categories.sort((x, y) => { return x.title.localeCompare(y.title); });

        // Sort category columns
        categories.forEach(c => {
            c.columns.sort((x, y) => { return x.Title.localeCompare(y.Title); });
        });

        return categories;
    }

    createColumnFilterOptions(allColumns: IColumnHeader[]): IComboBox[] {
        var uniqueCategories = this.getUniqueReportCategories(allColumns);
        var columnFilterOptions: IComboBox[] = uniqueCategories.map(c => { return { Name: c.title, Value: 0, Icon: c.icon }; });

        columnFilterOptions.forEach((option, i) => { option.Value = i + 1; });

        return columnFilterOptions;
    }

    getColumnDataSourceName(column: IColumnHeader): string {
        var displayName = column.dataSourceDefinition.TranslatedDisplayName
            ? column.dataSourceDefinition.TranslatedDisplayName
            : "Unknown";

        return displayName;
    }

    getColumnFilterOption(column: IColumnHeader, columnFilterOptions: IComboBox[]): IComboBox {
        var option: IComboBox;

        columnFilterOptions.forEach(cfo => {
            var displayName = this.getColumnDataSourceName(column);

            if (cfo.Name === displayName) {
                option = cfo;
            }
        });

        return option;
    }

    filtersToDefinition(filterOptions: IFilterOptions, definition: IDataDefinition) {
        if (!definition.Parameters) {
            definition.Parameters = {};
        }

        if (!filterOptions) {
            return;
        }

        if (filterOptions.dateFilter) {
            definition.Parameters.Start = filterOptions.dateFilter.start;
            definition.Parameters.End = filterOptions.dateFilter.end;
        }

        if (filterOptions.dateFilter && filterOptions.dateFilter.startTime && filterOptions.dateFilter.endTime) {
            definition.Parameters.TimeFilterStart = filterOptions.dateFilter.startTime;
            definition.Parameters.TimeFilterEnd = filterOptions.dateFilter.endTime;
        }

        if (filterOptions.dateFilter && filterOptions.dateFilter.filterType) {
            definition.Parameters.TimeFilterType = filterOptions.dateFilter.filterType;
        }

        if (filterOptions.vehicleFilter) {
            definition.Parameters.VehicleId = filterOptions.vehicleFilter.vehicle.Id;
        }

        if (filterOptions.driverFilter) {
            definition.Parameters.DriverId = filterOptions.driverFilter.driver.Id;
        }

        if (filterOptions.groupFilter) {
            // Todo: Clarify - May not want to align them both together...
            definition.Parameters.VehicleGroupId = filterOptions.groupFilter.group.Id;
            definition.Parameters.DriverGroupId = filterOptions.groupFilter.group.Id;
        }
    }

    findColumnsOfType(dataSourceType: string, columns: IColumnHeader[]): IColumnHeader[] {
        dataSourceType = dataSourceType.toLowerCase();

        let columnsToSearch = [];

        if (dataSourceType && columns) {
            columnsToSearch = columns.filter(column => { return column.DataSourceType.toLowerCase().indexOf(dataSourceType) !== -1 });
        }

        return columnsToSearch;
    }

    isMachineAndUserTZSame(): angular.IPromise<boolean> {

        let userOptions = this.sessionService.getItem<ICombinedPreferences>(SettingsName.UserOptions);

        return this.preferencesService.getTimezone(userOptions.Timezone.Value).then((timezone) => {

            let localOffset = moment().utcOffset();
            let userOffset = moment.tz(timezone.IanaId).utcOffset();
            return (userOffset - localOffset) == 0;
        });
    }

    findColumn(dataSource: string, dataSourceType: string, columns: IColumnHeader[]): IColumnHeader {
        dataSource = dataSource.toLowerCase();

        var foundColumn: IColumnHeader;

        const columnsToSearch = this.findColumnsOfType(dataSourceType, columns);

        columnsToSearch.forEach(header => {
            if (header.DataSourceValue.toLowerCase() === dataSource || header.Title.toLowerCase() == dataSource) {
                foundColumn = header;
            }
        });

        return copy(foundColumn);
    }

    stringifySchedulePreferences(schedule: IDynamicReportSchedule): void {
        if (!schedule?.Preferences) {
            schedule.Preferences = {};
        }

        schedule.PreferencesJson = JSON.stringify(schedule.Preferences)
    }

    parseSchedulePreferencesJson(schedule: IDynamicReportSchedule): void {
        if (!schedule?.PreferencesJson) {
            schedule.Preferences = {};
            return;
        }

        schedule.Preferences = JSON.parse(schedule.PreferencesJson);
    }

    stringifyScheduleParameters(schedule: IDynamicReportSchedule): void {
        if (!schedule?.Parameters) {
            schedule.Parameters = {};
        }

        schedule.ParametersJson = JSON.stringify(schedule.Parameters)
    }

    parseScheduleParametersJson(schedule: IDynamicReportSchedule): void {
        if (!schedule?.ParametersJson) {
            schedule.Parameters = {};
            return;
        }

        schedule.Parameters = JSON.parse(schedule.ParametersJson);
    }

    copyScheduleParametersToReport(schedule: IDynamicReportSchedule, report: IReportDefinition): void {
        report.Parameters = schedule.Parameters;
    }

    copySchedulePreferencesToReport(schedule: IDynamicReportSchedule, report: IReportDefinition): void {
        if (schedule.Preferences) {
            report.Currency = schedule.Preferences.Currency?.Value;
            report.VolumeMeasurement = schedule.Preferences.VolumeMeasurement?.Value;
            report.DistanceMeasurement = schedule.Preferences.DistanceMeasurement?.Value;
            report.TemperatureMeasurement = schedule.Preferences.TemperatureMeasurement?.Value;
            report.FuelConsumptionMeasurement = schedule.Preferences.FuelConsumptionMeasurement?.Value;
        }

        report.DurationFormat = schedule.DurationFormat;
    }

    copyReportParametersToSchedule(report: IReportDefinition, schedule: IDynamicReportSchedule): void {
        schedule.Parameters = {
            ...schedule.Parameters,
            ...report.Parameters
        }
        schedule.ParametersJson = JSON.stringify(schedule.Parameters);
    }

    copyReportPreferencesToSchedule(report: IReportDefinition, schedule: IDynamicReportSchedule): void {
        const newPreferences: IPreferencesContent = {
            Currency: {
                Value: report.Currency, Section: Section.Internationalisation
            },
            VolumeMeasurement: {
                Value: report.VolumeMeasurement, Section: Section.Internationalisation
            },
            DistanceMeasurement: {
                Value: report.DistanceMeasurement, Section: Section.Internationalisation
            },
            TemperatureMeasurement: {
                Value: report.TemperatureMeasurement, Section: Section.Internationalisation
            },
            FuelConsumptionMeasurement: {
                Value: report.FuelConsumptionMeasurement, Section: Section.Internationalisation
            }
        }

        schedule.Preferences = newPreferences;
        schedule.DurationFormat = report.DurationFormat;

        schedule.PreferencesJson = JSON.stringify(schedule.Preferences);
    }

    ensureUniqueReportColumnHeadersIds(reportDefinition: IReportDefinition) {
        if (!reportDefinition.TableDefinition.Headers?.length) {
            console.warn("No report table definition headers provided");
            return;
        }

        reportDefinition.TableDefinition.Headers.forEach(columnHeader => {
            this.ensureUniqueColumnId(columnHeader);
        });
    }

    ensureUniqueColumnHeadersIds(columnHeaders: IColumnHeader[]) {
        if (!columnHeaders?.length) {
            console.warn("No headers provided");
            return;
        }

        columnHeaders.forEach(columnHeader => {
            this.ensureUniqueColumnId(columnHeader);
        });
    }

    ensureUniqueColumnId(columnHeader: IColumnHeader) {
        if (!columnHeader) {
            console.warn("No columnHeader provided");
            return;
        }

        if (columnHeader.Id && !isNaN(columnHeader.Id)) {
            return;
        }

        columnHeader.Id = Math.floor(Math.random() * 20000000);
    }

    findAndReplaceRowTitle(reportDefinition: IReportDefinition, titleHeader: IColumnHeader) {
        const index = reportDefinition.TableDefinition.Headers.findIndex(header => header.IsRowTitle || header.DataSourceValue === "RowTitle");

        if (index) {
            reportDefinition.TableDefinition.Headers.splice(index, 1, titleHeader);
            return;
        }

        reportDefinition.TableDefinition.Headers.splice(0, 1, titleHeader);
    }

    setReportDefinitionRowTitle(reportDefinition: IReportDefinition, rowTitleHeader: IColumnHeader): void {
        if (!rowTitleHeader) {
            console.warn("No rowTitleHeader provided");
            return;
        }

        if (!reportDefinition) {
            console.warn("No reportDefinition provided");
            return;
        }

        const newHeaderColumn: IColumnHeader = copy(rowTitleHeader);

        newHeaderColumn.Title = this.getTitleForRowDataSource(reportDefinition);
        newHeaderColumn.IsRowTitle = true;
        newHeaderColumn.Sorted = true;

        this.ensureUniqueColumnId(newHeaderColumn);
        this.findAndReplaceRowTitle(reportDefinition, newHeaderColumn);
    }

    updateFilterOptionsForColumnHeaders(columnHeader: IColumnHeader[] = [], debug: boolean = false): void {
        columnHeader.forEach(columnHeader => {
            if (!!columnHeader.filterOptions) {
                return;
            }

            columnHeader.filterOptions = this.getColumnHeaderFilterOptions(columnHeader, debug);
        });
    }

    getColumnHeaderFilterOptions(columnHeader: IColumnHeader, debug: boolean = false): IColumnFilterOptions {

        const filterOptions: IColumnFilterOptions = {
            canFilterColumn: false,
            showFilterValueOperators: false,
            showTimeFilter: false,
            filterTimeHour: null,
            filterTimeMinute: null
        };

        if (!columnHeader) {
            return filterOptions;
        }

        if (columnHeader.ColumnDefinition.Type === ColumnDataType.Duration) {
            const parsedTimeSpan: number = this.veriDate.parseTimeSpan(columnHeader.FilterValue);
            const timeFilter = this.veriDate.createMoment(new Date());

            this.veriDate.setMomentToMinuteOfDay(timeFilter, parsedTimeSpan / 60);

            filterOptions.filterTimeHour = timeFilter.hour();
            filterOptions.filterTimeMinute = timeFilter.minute();
            filterOptions.showTimeFilter = true;
        }

        const typesWithFilterValueOperators: ColumnDataType[] = [
            ColumnDataType.Double,
            ColumnDataType.Int,
            ColumnDataType.Score,
            ColumnDataType.Percent,
            ColumnDataType.Duration
        ];

        const typesWithoutFilterValueOperators: ColumnDataType[] = [
            ColumnDataType.String,
            ColumnDataType.List,
            ColumnDataType.Location
        ];

        filterOptions.canFilterColumn = typesWithFilterValueOperators.includes(columnHeader.ColumnDefinition.Type) || typesWithoutFilterValueOperators.includes(columnHeader.ColumnDefinition.Type);
        filterOptions.showFilterValueOperators = typesWithFilterValueOperators.includes(columnHeader.ColumnDefinition.Type);

        // display DOM elements
        filterOptions.displayTimeFilterControls = filterOptions.canFilterColumn && filterOptions.showFilterValueOperators && filterOptions.showTimeFilter;
        filterOptions.displayFilterValueControl = filterOptions.canFilterColumn && filterOptions.showFilterValueOperators && !filterOptions.showTimeFilter;
        filterOptions.displayValueContainsControl = filterOptions.canFilterColumn && !filterOptions.showFilterValueOperators;
        filterOptions.displayGenericFilterValueControls = filterOptions.displayFilterValueControl || filterOptions.displayValueContainsControl;


        return filterOptions;
    }

    getDataSourceDefinitionsAndMapToColumns(columns: IColumnHeader[]): Promise<void> {
        return (this.getDataSourceDefinitions() as Promise<IDataSourceDefinition[]>).then((dataSourceDefinition: IDataSourceDefinition[]) => {
            this.mapDataSourceDefinitionsToColumns(columns, dataSourceDefinition);
            this.formatColumnsForDisplay(columns);
        });
    }

    getTitleForRowDataSource(reportDefinition: IReportDefinition): string {
        if (!reportDefinition) {
            console.warn("No reportDefinition provided");
            return;
        }

        let dataSource = reportDefinition.RowDataSource;

        if (Number.isNaN(dataSource) || typeof (dataSource) === "string") {
            dataSource = Number.parseInt(reportDefinition.RowDataSource.toString());
        }

        switch (dataSource) {
            case ReportTableRowDataSource.Day:
                return "Day";
            case ReportTableRowDataSource.Week:
                return "Week";
            case ReportTableRowDataSource.Driver:
                return "Driver";
            case ReportTableRowDataSource.Vehicle:
                return "Vehicle";
            case ReportTableRowDataSource.DriverGroup:
                return "Driver Group";
            case ReportTableRowDataSource.VehicleGroup:
                return "Vehicle Group";
            case ReportTableRowDataSource.Geofence:
                return "Geofence";
            case ReportTableRowDataSource.Journey:
                return "Journey";
            case ReportTableRowDataSource.Route:
                return "Route";
            case ReportTableRowDataSource.StationaryLocations:
                return "Stationary Location";
            case ReportTableRowDataSource.TemperatureRecord:
                return "Temperature Record";
            case ReportTableRowDataSource.TrailerAssociation:
                return "Trailer Association";
        }

        return ReportTableRowDataSource[dataSource];
    }

    formatReportLog(log: IReportHistory) {
        let reportTile = "Report";
        if (log.ReportTitle) {
            reportTile = log.ReportTitle.replace(/[^\w\s]/gi, "");
        }

        if (log.ThumbnailFileGuid) {
            log.thumbnailUrl = `${this.apiUrl}/file/thumbnail/${log.ThumbnailFileGuid}`;
        }

        log.pdfUrl = `${this.apiUrl}/file/${reportTile}/${log.PdfFileGuid}`;
        log.excelUrl = `${this.apiUrl}/file/${reportTile}/${log.ExcelFileGuid}`;
        log.csvUrl = `${this.apiUrl}/file/${reportTile}/${log.CsvFileGuid}`;

        if (!log.ReportTitle) {
            log.ReportTitle = "...";
        }

        this.formatParametersToLocalTimezone(log.Parameters);
    }

    getSortSettings(report: IReportDefinition): IHeaderSortSettings {
        const headerSortSettings: IHeaderSortSettings = {
            headerId: null,
            ascending: null
        }

        if (!report?.TableDefinition?.Headers?.length) {
            console.warn("Couldn't create sort settings due to no headers");
            return headerSortSettings;
        }

        const sortHeader = report.TableDefinition.Headers.find(header => header.Sorted);

        if (!sortHeader) {
            console.warn("Couldn't create sort settings due to no sort header");
            return headerSortSettings;
        }

        headerSortSettings.headerId = sortHeader.Id;
        headerSortSettings.ascending = sortHeader.SortAscending;

        return headerSortSettings;
    }

    shouldShowGeofenceFilters(report: IReportDefinition): boolean {
        return report.ReportType.toLowerCase().includes("geofence");
    }
    // ---

    private isNullOrDefault(reportParameters: IReportParameters): boolean {
        if (!reportParameters.TimeFilterStart || !reportParameters.TimeFilterEnd) {
            return true;
        }

        if (reportParameters.TimeFilterStart === "00:00:00" && reportParameters.TimeFilterEnd === "00:00:00") {
            return true;
        }

        if (reportParameters.TimeFilterStart === "00:00:00" && reportParameters.TimeFilterEnd === "23:59:59") {
            return true;
        }

        return false;
    }

    private timespanToUTC(time: string): string {
        return moment(time, "HH:mm:ss").local().utc().format("HH:mm:ss");
    }

    private timespanToLocal(time: string): string {
        return moment.utc(time, "HH:mm:ss").local().format("HH:mm:ss");
    }

    private getDocumentExtension(exportType: ExportType): string {
        switch (exportType) {
            case ExportType.PDF:
                return ".pdf";

            case ExportType.CSV:
                return ".csv";

            case ExportType.Excel:
                return ".xlsx";

            default:
                return "";
        }
    }

    private setParameterDefinitionsFromReport(reportDefinitions: IReportDefinition, parameterDefinitions: IReportParameterDefinition[]): void {
        if (!reportDefinitions || !reportDefinitions.Parameters || !reportDefinitions.Parameters.CustomParameters || !parameterDefinitions) {
            return;
        }
        parameterDefinitions.forEach(parameter => {
            reportDefinitions.Parameters.CustomParameters.forEach(reportParameter => {
                if (parameter.ParameterType === reportParameter.ParameterType) {
                    parameter.Active = reportParameter.Active;
                    parameter.Value = reportParameter.Value;
                }
            });
        });
    }

    private getColumnTypeDisplayName(type: ColumnDataType): string {
        if (type === ColumnDataType.Double || type === ColumnDataType.Int) {
            return "Number";
        }

        if (type === ColumnDataType.Duration) {
            return "Duration";
        }

        return ColumnDataType[type];
    }

    private rowDataSourceToCustomParameterMappings: IRowDataSourceToCustomParameterMapping[] = [
        {
            rowDataSource: ReportTableRowDataSource.DriverGroup,
            mappings: [{
                parameterName: this.translationService.t("Reports.CreateUnassignedGroupRow"),
                valueType: ReportParameterValueType.Boolean,
                ParameterType: ReportParameterType.CreateUnassignedGroupRow,
                defaultValue: false,
                Value: false,
                Active: false,
            }]
        },
        {
            rowDataSource: ReportTableRowDataSource.VehicleGroup,
            mappings: [{
                parameterName: this.translationService.t("Reports.CreateUnassignedGroupRow"),
                valueType: ReportParameterValueType.Boolean,
                ParameterType: ReportParameterType.CreateUnassignedGroupRow,
                defaultValue: false,
                Value: false,
                Active: false,
            }]
        },
        {
            rowDataSource: ReportTableRowDataSource.VehicleDepot,
            mappings: [{
                parameterName: this.translationService.t("Reports.CreateUnassignedGroupRow"),
                valueType: ReportParameterValueType.Boolean,
                ParameterType: ReportParameterType.CreateUnassignedGroupRow,
                defaultValue: false,
                Value: false,
                Active: false,
            }]
        }
    ]

    private reportTypeToCustomParameterMappings: IReportTypeToCustomParameterMapping[] = [
        {
            reportType: "Journey",
            mappings: [
                {
                    parameterName: this.translationService.t("Reports.MinimumDistance"),
                    valueType: ReportParameterValueType.Number,
                    conversionType: UnitConversionType.Distance,
                    ParameterType: ReportParameterType.MinimumDistance,
                    defaultValue: 0,
                    Value: 0,
                    Active: false,
                    minValue: 0,
                },
                {
                    parameterName: this.translationService.t("Reports.MinimumFuelUsed"),
                    valueType: ReportParameterValueType.Number,
                    conversionType: UnitConversionType.FuelCapacity,
                    ParameterType: ReportParameterType.MinimumFuelUsed,
                    defaultValue: 0,
                    Value: 0,
                    Active: false,
                    minValue: 0,
                },
                {
                    parameterName: this.translationService.t("Reports.MinimumSpeed"),
                    valueType: ReportParameterValueType.Number,
                    conversionType: UnitConversionType.Speed,
                    ParameterType: ReportParameterType.MinimumSpeed,
                    defaultValue: 0,
                    Value: 0,
                    Active: false,
                    minValue: 0,
                },
                {
                    parameterName: this.translationService.t("Reports.MinimumIdleTime"),
                    valueType: ReportParameterValueType.Number,
                    conversionType: UnitConversionType.Time,
                    ParameterType: ReportParameterType.MinimumIdleTime,
                    defaultValue: 0,
                    Value: 0,
                    Active: false,
                    minValue: 0,
                },
                {
                    parameterName: this.translationService.t("Reports.ExcludeGpsJourneys"),
                    valueType: ReportParameterValueType.Boolean,
                    ParameterType: ReportParameterType.ExcludeGpsJourneys,
                    defaultValue: false,
                    Value: false,
                    Active: false
                },
                {
                    parameterName: this.translationService.t("Reports.ExcludeInconsequentialJourneys"),
                    valueType: ReportParameterValueType.Boolean,
                    ParameterType: ReportParameterType.ExcludeInconsequentialJourneys,
                    defaultValue: false,
                    Value: false,
                    Active: false
                },
                {
                    parameterName: this.translationService.t("Reports.ShowOnlyBusinessJourneys"),
                    valueType: ReportParameterValueType.Boolean,
                    ParameterType: ReportParameterType.ShowOnlyBusinessJourneys,
                    defaultValue: false,
                    Value: false,
                    Active: false
                },
                {
                    parameterName: this.translationService.t("Reports.HideLocationAddressShowonlyLatitudeandLongitude"),
                    valueType: ReportParameterValueType.Boolean,
                    ParameterType: ReportParameterType.LatLongOnly,
                    defaultValue: false,
                    Value: false,
                    Active: false
                }
            ]
        },
        {
            reportType: "Alcolock",
            mappings: [
                {
                    parameterName: this.translationService.t("Reports.EventGroup"),
                    valueType: ReportParameterValueType.Select,
                    ParameterType: ReportParameterType.ShowOnlyAlcolockEventGroup,
                    defaultValue: -1,
                    Value: -1,
                    Active: false,
                    options: [
                        { Name: "Any", Value: -1 },
                        { Name: "Fail", Value: AlcolockEventGroup.Fail },
                        { Name: "Circumvention", Value: AlcolockEventGroup.Circumvention },
                        { Name: "Pass", Value: AlcolockEventGroup.Pass },
                        { Name: "SignificantEvent", Value: AlcolockEventGroup.SignificantEvent }
                    ]
                }
            ]
        },
        {
            reportType: "StationaryLocation",
            mappings: [
                {
                    parameterName: this.translationService.t("Reports.MinimumFuelLevelChange") + "(%)",
                    valueType: ReportParameterValueType.Number,
                    ParameterType: ReportParameterType.MinimumFuelLevelChange,
                    defaultValue: 0,
                    Value: 0,
                    Active: false,
                    minValue: 0,
                    maxValue: 100
                },
                {
                    parameterName: this.translationService.t("Reports.ExcludeGpsJourneys"),
                    valueType: ReportParameterValueType.Boolean,
                    ParameterType: ReportParameterType.ExcludeGpsJourneys,
                    defaultValue: true,
                    Value: true,
                    Active: true
                },
                {
                    parameterName: this.translationService.t("Reports.HideLocationAddressShowonlyLatitudeandLongitude"),
                    valueType: ReportParameterValueType.Boolean,
                    ParameterType: ReportParameterType.LatLongOnly,
                    defaultValue: false,
                    Value: false,
                    Active: false
                }
            ]
        },
        {
            reportType: "VehicleStatus",
            mappings: [
                {
                    parameterName: this.translationService.t("Reports.IncludeOnlyInstalledCameras"),
                    valueType: ReportParameterValueType.Boolean,
                    ParameterType: ReportParameterType.IncludeOnlyInstalledCameras,
                    defaultValue: false,
                    Value: false,
                    Active: false
                },
                {
                    parameterName: this.translationService.t("Reports.HideLocationAddressShowonlyLatitudeandLongitude"),
                    valueType: ReportParameterValueType.Boolean,
                    ParameterType: ReportParameterType.LatLongOnly,
                    defaultValue: false,
                    Value: false,
                    Active: false
                }
            ]
        },
        {
            reportType: "CommonEvent",
            mappings: [
                {
                    parameterName: this.translationService.t("Reports.Reason"),
                    valueType: ReportParameterValueType.Select,
                    ParameterType: ReportParameterType.ReasonCode,
                    defaultValue: 54,
                    Value: 54,
                    Active: false,
                    options: [
                        { Name: this.translationService.t("Reports.Speeding"), Value: ReasonCode.ContextualSpeeding },
                        { Name: this.translationService.t("Reports.HarshAcceleration"), Value: ReasonCode.HarshAcceleration },
                        { Name: this.translationService.t("Reports.HarshCornering"), Value: ReasonCode.HarshCornering },
                        { Name: this.translationService.t("Reports.HarshBraking"), Value: ReasonCode.HarshBraking },
                        { Name: this.translationService.t("Reports.BadAnticipation"), Value: ReasonCode.BadAnticipation },
                        { Name: this.translationService.t("Reports.Idling"), Value: ReasonCode.Idling }
                    ]
                },
                {
                    parameterName: this.translationService.t("Reports.MinimumEventTime"),
                    valueType: ReportParameterValueType.Number,
                    conversionType: UnitConversionType.Time,
                    ParameterType: ReportParameterType.MinimumEventTime,
                    defaultValue: 0,
                    Value: 0,
                    Active: false,
                    minValue: 0,
                },
                {
                    parameterName: this.translationService.t("Reports.HideLocationAddressShowonlyLatitudeandLongitude"),
                    valueType: ReportParameterValueType.Boolean,
                    ParameterType: ReportParameterType.LatLongOnly,
                    defaultValue: false,
                    Value: false,
                    Active: false
                }
            ]
        },
        {
            reportType: "GeofenceVisit",
            mappings: [
                {
                    parameterName: this.translationService.t("Reports.ShowDurationPastEndTime"),
                    valueType: ReportParameterValueType.Boolean,
                    ParameterType: ReportParameterType.ShowDurationPastEndTime,
                    defaultValue: false,
                    Value: false,
                    Active: false
                },
                {
                    parameterName: this.translationService.t("Reports.HideLocationAddressShowonlyLatitudeandLongitude"),
                    valueType: ReportParameterValueType.Boolean,
                    ParameterType: ReportParameterType.LatLongOnly,
                    defaultValue: false,
                    Value: false,
                    Active: false
                },
                {
                    parameterName: this.translationService.t("Reports.CurrentlyDwellingAssestsOnly"),
                    valueType: ReportParameterValueType.Number,
                    ParameterType: ReportParameterType.InProgressGeofenceVisits,
                    defaultValue: 0,
                    Value: 0,
                    Active: false,
                    minValue: 0,
                    conversionLabel: this.translationService.t("Reports.ForNDays")

                }]
        },
        {
            reportType: "Temperature",
            mappings: [
                {
                    parameterName: this.translationService.t("Reports.HideProbes"),
                    valueType: ReportParameterValueType.TemperatureProbes,
                    ParameterType: ReportParameterType.HiddenProbes,
                    defaultValue: [],
                    Value: [],
                    Active: true
                }]
        },
        {
            reportType: "VehicleInput",
            mappings: [
                {
                    parameterName: this.translationService.t("Reports.HideLocationAddressShowonlyLatitudeandLongitude"),
                    valueType: ReportParameterValueType.Boolean,
                    ParameterType: ReportParameterType.LatLongOnly,
                    defaultValue: false,
                    Value: false,
                    Active: false
                }
            ]
        },
        {
            reportType: "Camera",
            mappings: [
                {
                    parameterName: this.translationService.t("Reports.HideLocationAddressShowonlyLatitudeandLongitude"),
                    valueType: ReportParameterValueType.Boolean,
                    ParameterType: ReportParameterType.LatLongOnly,
                    defaultValue: false,
                    Value: false,
                    Active: false
                }
            ]
        },
        {
            reportType: "VehicleActivity",
            mappings:
                [
                    {
                        parameterName: this.translationService.t("Reports.ExcludeVehiclesWithoutActivity"),
                        valueType: ReportParameterValueType.Boolean,
                        ParameterType: ReportParameterType.ExcludeVehiclesWithoutActivity,
                        defaultValue: false,
                        Value: false,
                        Active: false
                    },
                ]
        },
        {
            reportType: "BrakePerformanceValue",
            mappings:
                [
                    {
                        parameterName: this.translationService.t("Reports.VehicleType"),
                        valueType: ReportParameterValueType.Select,
                        ParameterType: ReportParameterType.VehicleType,
                        defaultValue: VehicleType.Trailer,
                        Value: null,
                        Active: false,
                        // Show all VehicleType, except for the UI-defined option None, which has a negative value
                        options: enumNames()(VehicleType).filter(vt => VehicleType[vt] >= 0).map(vt => ({ Name: this.translationService.translate(`VehicleTypeEnum.${vt}`), Value: VehicleType[vt] }))
                    },
                ]
        }
    ];

    private requiresSelector(reportDefinitions: IReportDefinition, reportTypes: string[]): boolean {

        var requiresSelector = false;

        if (reportDefinitions && reportDefinitions.TableDefinition) {
            reportDefinitions.TableDefinition.Headers.forEach(header => {
                if (reportTypes.indexOf(header.ReportType) >= 0) {
                    requiresSelector = true;
                }
            });
        }

        return requiresSelector;
    }

    mapPreferences(schedule: IDynamicReportSchedule, report: IReportDefinition, direction: "to-report" | "to-schedule"): void {
        if (!schedule) {
            console.warn("No schedule provided");
            return;
        }

        if (!report) {
            console.warn("No report provided");
            return;
        }

        switch (direction) {
            case "to-schedule":

                schedule.Preferences = {} as any;
                schedule.DurationFormat = report?.DurationFormat;

                this.mapPrefs((preference: string) => {
                    schedule.Preferences[preference] = {
                        Value: report[preference],
                        Section: Section.Report,
                    };
                });

                break;

            case "to-report":

                report.DurationFormat = schedule?.DurationFormat;

                this.setReportPreferences(report, schedule.Preferences)

                break;
        }
    }

    private mapPrefs(mappingFn: (preference: string) => void): void {
        const preferencesFields: Array<Partial<keyof IReportDefinition>> = [
            "DistanceMeasurement",
            "TemperatureMeasurement",
            "VolumeMeasurement",
            "FuelConsumptionMeasurement",
            "Currency",
        ];
        preferencesFields.forEach((preference) => mappingFn(preference));
    }

    private setReportPreferences(report: IReportDefinition, preferences: IPreferencesContent): void {

        if (!preferences) {
            return;
        }

        this.mapPrefs((preference: string) => {
            if (preferences[preference]) {
                report[preference] = preferences[preference].Value;
            }
        });

    }
}
